import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../api';
import { booking } from '../api/booking';
import { mapsApi } from '../api/maps';
import DeliveryPin from '../components/DeliveryPin';
import ParcelSeen from '../components/customer/ParcelSeen';
import InGoMap from '../components/maps/InGoMap';
import { clientPaymentLine } from '../lib/clientPayment';
import { drawingFor, useVehicleTypes } from '../lib/useVehicleTypes';
import { formatGBP } from '../lib/currency';
import { refusalMessage } from '../api/errors';
import './bookRide.css';

/** How often the client's screen reads the delivery while it moves. */
const POLL_MS = 5000;

/** The steps a client follows, in order; each lit when the server says it happened. */
const STEPS = [
  { key: 'requested_at', label: 'Booked' },
  { key: 'accepted_at', label: 'A rider is on the way to pick it up' },
  { key: 'picked_up_at', label: 'Picked up, on the way to the drop-off' },
  { key: 'delivered_at', label: 'Delivered' },
];

const FINISHED = ['delivered', 'completed', 'cancelled'];

/** While a rider carries it: the rider's position is asked for with the delivery. */
const LIVE = ['accepted', 'picked_up'];

/** "about 6 min", from the road engine's estimate. */
function minutes(seconds) {
  const m = Math.max(1, Math.round(Number(seconds) / 60));

  return `about ${m} min`;
}

/**
 * Whether the client may rate this delivery's rider now (ingo-e0's Ratings,
 * `GET customer/me/deliveries/{id}/rating`): false on any answer but a clear yes.
 */
async function mayRate(id) {
  try {
    const { data } = await api.get(`/customer/me/deliveries/${encodeURIComponent(id)}/rating`);

    return data?.may_rate === true;
  } catch {
    return false;
  }
}

function when(iso) {
  if (!iso) return '';

  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * One delivery booked on InGo's API, followed by the client (the mobile
 * plan's M5): read every few seconds until it is finished; the code to give
 * the rider at the door, shown only to the client; the route and the price;
 * and cancelling while no rider has it yet. Its map is InGo's (M8): the road
 * between the pins, and, while a rider carries it, where the rider is now and
 * how far away (M7), asked with each read. The rider's card says who to look
 * for, with a number to call while the job is live.
 */
export default function ApiDeliveryTracking({ id }) {
  const navigate = useNavigate();
  const { vehicles: vehicleTypes } = useVehicleTypes();
  const [delivery, setDelivery] = useState(null);
  const [status, setStatus] = useState('loading');
  const [error, setError] = useState('');
  const [cancelling, setCancelling] = useState(false);
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [road, setRoad] = useState(null);
  const [tracking, setTracking] = useState(null);
  const [rateable, setRateable] = useState(false);

  const read = useCallback(async () => {
    try {
      const d = await booking.delivery(id);
      setDelivery(d);
      setStatus('ready');
      setError('');
      if (LIVE.includes(d.status)) {
        // The rider's position is extra: a failed read keeps the last one.
        mapsApi.tracking(id).then(setTracking, () => {});
      } else {
        setTracking(null);
      }
    } catch (e) {
      if (e?.status === 404) {
        setStatus('missing');
        return;
      }
      // A failed read keeps the last known state on screen and says so.
      setError(refusalMessage(e));
      setStatus((current) => (current === 'loading' ? 'failed' : current));
    }
  }, [id]);

  useEffect(() => {
    void read();
  }, [read]);

  const finished = delivery ? FINISHED.includes(delivery.status) : false;

  useEffect(() => {
    if (finished) return undefined;
    const timer = window.setInterval(() => void read(), POLL_MS);

    return () => window.clearInterval(timer);
  }, [finished, read]);

  // The road between the pins, asked once: InGo's engine, never Google's.
  const pickupAt = delivery?.pickup ? `${delivery.pickup.latitude},${delivery.pickup.longitude}` : '';
  const dropoffAt = delivery?.dropoff ? `${delivery.dropoff.latitude},${delivery.dropoff.longitude}` : '';
  useEffect(() => {
    if (!pickupAt || !dropoffAt) return;
    let gone = false;
    const [a, b] = [pickupAt, dropoffAt].map((s) => s.split(',').map(Number));
    mapsApi.route([{ lat: a[0], lng: a[1] }, { lat: b[0], lng: b[1] }]).then(
      (r) => !gone && setRoad(r?.polyline ?? null),
      () => {},
    );

    return () => {
      gone = true;
    };
  }, [pickupAt, dropoffAt]);

  const completed = delivery ? ['delivered', 'completed'].includes(delivery.status) : false;
  useEffect(() => {
    if (!completed) return;
    let gone = false;
    mayRate(id).then((yes) => !gone && setRateable(yes));

    return () => {
      gone = true;
    };
  }, [completed, id]);

  const cancel = async () => {
    setCancelling(true);
    setError('');
    try {
      setDelivery(await booking.cancel(id, 'Cancelled by the client in the app.'));
      setConfirmCancel(false);
    } catch (e) {
      setError(refusalMessage(e));
    } finally {
      setCancelling(false);
    }
  };

  const header = (
    <header className="br-nav br-nav--stacked">
      <Link to="/home" className="br-nav__back" aria-label="Home">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden>
          <path d="M15.5 18.5L8.5 12l7-7.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </Link>
      <div className="br-nav__center">
        <h1 className="br-nav__title">Your delivery</h1>
        <p className="br-nav__step">{delivery?.reference || ''}</p>
      </div>
      <span className="br-nav__spacer" aria-hidden />
    </header>
  );

  if (status === 'loading' || status === 'missing' || (status === 'failed' && !delivery)) {
    return (
      <div className="br-page br-page--form">
        {header}
        <div className="br-scroll br-scroll--form">
          <section className="br-pd-card" role={status === 'loading' ? 'status' : 'alert'}>
            <p className="br-pe-service__sub">
              {status === 'loading'
                ? 'Opening your delivery…'
                : status === 'missing'
                  ? 'This delivery is not on your account.'
                  : error || 'Your delivery could not be opened.'}
            </p>
          </section>
        </div>
        <div className="br-footer">
          <button type="button" className="br-confirm" onClick={() => (status === 'failed' ? void read() : navigate('/home'))}>
            {status === 'failed' ? 'Try again' : 'Home'}
          </button>
        </div>
      </div>
    );
  }

  const d = delivery;
  const cancelled = d.status === 'cancelled';
  const pu = d.pickup;
  const dr = d.dropoff;
  const pins = pu && dr ? [{ role: 'pickup', lat: pu.latitude, lng: pu.longitude }, { role: 'dropoff', lat: dr.latitude, lng: dr.longitude }] : [];
  const live = LIVE.includes(d.status);
  const position = live && tracking?.position ? tracking.position : null;
  // The fix's own time paces the glide (InGoMap, M7).
  const rider = position ? { lat: position.latitude, lng: position.longitude, heading: position.heading_degrees ?? null, at: position.recorded_at ? Date.parse(position.recorded_at) : undefined } : null;
  const estimate = live && tracking?.estimate ? tracking.estimate : null;
  const card = d.rider;
  // The rider drawn as their vehicle type's drawing (the card names the type), blue on the way, grey when the phone has gone quiet.
  const riderVehicle = rider ? { vehicle: drawingFor(card?.vehicle, vehicleTypes), state: position.stale ? 'silent' : 'delivering' } : null;
  const total = formatGBP(d.price.total_minor / 100);

  return (
    <div className="br-page br-page--form">
      {header}
      {pins.length === 2 ? (
        <section className="br-track-map" aria-label="Route map">
          <InGoMap pins={pins} route={road} rider={rider} riderVehicle={riderVehicle} label={rider ? 'Your rider, the pickup and the drop-off' : 'Pickup to drop-off'} height="100%" bleed />
        </section>
      ) : null}
      <div className="br-scroll br-scroll--form">
        {error ? (
          <p className="br-pe-distance" role="alert" style={{ color: 'var(--ingo-danger, #b91c1c)' }}>
            <span className="br-pe-distance__val">{error}</span>
          </p>
        ) : null}

        <section className="br-pd-card" aria-label="Where it is">
          <h2 className="br-pd-heading" aria-live="polite">
            {d.status_label}
          </h2>
          {cancelled ? (
            <p className="br-pe-service__sub">
              Cancelled at {when(d.cancelled_at)}
              {d.cancel_reason ? `: ${d.cancel_reason}` : '.'}
            </p>
          ) : (
            <ol className="br-loc-list" aria-label="Steps">
              {STEPS.map((step) => {
                const at = d[step.key];

                return (
                  <li key={step.key} className="br-loc-row br-pe-loc__row" aria-current={at ? 'step' : undefined}>
                    <span className={`br-loc-row__dot ${at ? 'br-loc-row__dot--pickup' : 'br-loc-row__dot--drop'}`} aria-hidden />
                    <div className="br-loc-row__main">
                      <span className="br-loc-row__label">{at ? when(at) : 'Not yet'}</span>
                      <p className="br-pe-loc__addr" style={{ opacity: at ? 1 : 0.55 }}>
                        {step.label}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ol>
          )}
        </section>

        {!finished && d.code ? (
          <section className="br-pd-card" aria-label="Your delivery code">
            <h2 className="br-pd-heading">Your delivery code</h2>
            <p className="br-pe-service__sub">Give it to the rider at the door, and only then: it proves the parcel reached you.</p>
            <DeliveryPin value={d.code} readOnly idPrefix="api-code" ariaLabel="Your delivery code" />
          </section>
        ) : null}

        {card && !cancelled ? (
          <section className="br-pd-card" aria-label="Your rider">
            <h2 className="br-pd-heading">Your rider</h2>
            <p className="br-pe-loc__addr">
              {card.first_name}, {card.vehicle_details || card.vehicle}
              {card.plate ? ` (${card.plate})` : ''}
            </p>
            {estimate ? (
              <p className="br-pe-service__sub" aria-live="polite">
                {d.status === 'accepted' ? 'To the pickup: ' : 'To you: '}
                {minutes(estimate.duration_seconds)}
              </p>
            ) : null}
            {position?.stale ? <p className="br-pe-service__sub">Last heard from {minutes(position.age_seconds)} ago.</p> : null}
            {card.phone ? (
              <a className="br-confirm br-confirm--link" style={{ marginTop: 12 }} href={`tel:${card.phone}`}>
                Call {card.first_name}
              </a>
            ) : null}
          </section>
        ) : null}

        <section className="br-pd-card br-pe-route" aria-label="Route">
          <h2 className="br-pd-heading">Route</h2>
          <div className="br-loc-list br-pe-loc" role="list">
            <div className="br-loc-row br-pe-loc__row" role="listitem">
              <span className="br-loc-row__dot br-loc-row__dot--pickup" aria-hidden />
              <div className="br-loc-row__main">
                <span className="br-loc-row__label">Pickup</span>
                <p className="br-pe-loc__addr">{pu?.address || '—'}</p>
              </div>
            </div>
            <div className="br-loc-row br-pe-loc__row" role="listitem">
              <span className="br-loc-row__dot br-loc-row__dot--drop" aria-hidden />
              <div className="br-loc-row__main">
                <span className="br-loc-row__label">Drop-off</span>
                <p className="br-pe-loc__addr">{dr?.address || '—'}</p>
              </div>
            </div>
          </div>
          <p className="br-pe-distance">
            <span className="br-pe-distance__lab">{clientPaymentLine(d)}</span>
            <span className="br-pe-distance__val">{total}</span>
          </p>
        </section>

        <ParcelSeen delivery={d} onChanged={() => void read()} />
      </div>

      <div className="br-footer">
        {d.may_cancel && !confirmCancel ? (
          <button type="button" className="br-confirm" style={{ background: 'transparent', color: 'inherit', border: '1px solid currentColor' }} onClick={() => setConfirmCancel(true)}>
            Cancel delivery
          </button>
        ) : null}
        {d.may_cancel && confirmCancel ? (
          <button type="button" className="br-confirm" onClick={() => void cancel()} disabled={cancelling}>
            {cancelling ? 'Cancelling…' : 'Yes, cancel this delivery'}
          </button>
        ) : null}
        {rateable ? (
          <Link to={`/rate/${d.id}`} className="br-confirm br-confirm--link" style={{ marginBottom: 8 }}>
            Rate your rider
          </Link>
        ) : null}
        {!d.may_cancel ? (
          <button type="button" className="br-confirm" onClick={() => navigate('/home')}>
            {finished ? 'Done' : 'Home'}
          </button>
        ) : null}
      </div>
    </div>
  );
}
